import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { PersonalSelectors } from 'stores/personal/personal.module';
import { VehicleSelectors } from 'stores/vehicle/vehicle.module';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    selectGenericDropOffPages,
    selectDropOffState,
} from 'sharedStores/drop-off/drop-off.selectors';
import { Page } from 'sharedStores/cms/models/page';
import { DropOffCode } from 'sharedEnums/drop-off-code';
import { QuoteControllerSelectors } from 'sharedStores/quote-controller/quote-controller.module';
import { CoverType } from 'enums/cover-type';
import { FormItem } from 'sharedStores/cms/models/pages/form/form-item';
import { FormButton } from 'sharedStores/cms/models/pages/form/form-button';
import { IDropOffService } from 'sharedModels/interfaces/dropoff-service';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class DropOffService implements IDropOffService {
    genericDropOffPages$ = this.store$.select(selectGenericDropOffPages);
    coverTypeDialogOpened = this.store$.selectSignal(
        QuoteControllerSelectors.coverTypeDialogOpened,
    );
    vehicle = this.store$.selectSignal(VehicleSelectors.selectVehicle);
    regularDriver$ = this.store$.selectSignal(PersonalSelectors.selectRegularDriver);
    dropOffState = this.store$.selectSignal(selectDropOffState);

    defaultUnderAgeRiskPage: Page;
    defaultReferToManagerPage: Page;
    defaultUnacceptableRiskPage: Page;
    defaultHasMultiRiskItemsPage: Page;
    defaultPolicyExistPage: Page;
    defaultUnderSixMonthsPage: Page;
    defaultPolicyCancelledPage: Page;

    constructor(private store$: Store) {
        this.genericDropOffPages$.pipe(untilDestroyed(this)).subscribe((data: Page[]) => {
            this.defaultReferToManagerPage = data?.find((page) => page.defaultReferToManager);
            this.defaultUnacceptableRiskPage = data?.find((page) => page.defaultUnacceptableRisk);
            this.defaultHasMultiRiskItemsPage = data?.find((page) => page.defaultHasMultiRiskItems);
            this.defaultPolicyExistPage = data?.find((page) => page.defaultPolicyExist);
            this.defaultUnderAgeRiskPage = data?.find((page) => page.defaultUnderAgeRisk);
            this.defaultUnderSixMonthsPage = data?.find((page) => page.defaultUnderSixMonths);
            this.defaultPolicyCancelledPage = data?.find((page) => page.defaultPolicyCancelled);
        });
    }

    checkPolicyExists(): boolean {
        return this.dropOffState()?.code === DropOffCode.POLICY_EXISTS;
    }

    checkDropOffPageExist(): { isDropOff: boolean; pageRoute: string } {
        switch (this.dropOffState()?.code) {
            case DropOffCode.SUSPENDED:
                return { isDropOff: true, pageRoute: this.defaultReferToManagerPage.route };

            case DropOffCode.REFER_TO_MANAGER:
                if (this.dropOffState()?.description === 'Cancelled policy (Same Brand)') {
                    return { isDropOff: true, pageRoute: this.defaultUnderSixMonthsPage.route };
                } else {
                    return { isDropOff: true, pageRoute: this.defaultReferToManagerPage.route };
                }

            case DropOffCode.UNACCEPTABLE_RISK:
                if (
                    this.dropOffState()?.description ===
                    'Previous Internal Fraud within 5 years - PH'
                ) {
                    return { isDropOff: true, pageRoute: this.defaultPolicyCancelledPage.route };
                } else {
                    return { isDropOff: true, pageRoute: this.defaultUnacceptableRiskPage.route };
                }
            case DropOffCode.HAS_MULTI_RISK_ITEMS:
                return { isDropOff: true, pageRoute: this.defaultHasMultiRiskItemsPage.route };
            case DropOffCode.POLICY_EXISTS:
                return { isDropOff: true, pageRoute: this.defaultPolicyExistPage.route };
            case DropOffCode.UNDER_AGE_RISK:
                return { isDropOff: true, pageRoute: this.defaultUnderAgeRiskPage.route };
            default:
                return { isDropOff: false, pageRoute: '' };
        }
    }

    checkDropOffRouteExist(dropOffRoutes: FormItem<FormButton>[]): {
        isResumeDropOff: boolean;
        pageRoute: string;
    } {
        const dropOffItem = dropOffRoutes.find(
            (dropOffItem) => dropOffItem.details.label === this.dropOffState()?.code,
        );
        if (dropOffItem) {
            return { isResumeDropOff: true, pageRoute: dropOffItem.details.route.pageRoute };
        }
        return { isResumeDropOff: false, pageRoute: '' };
    }

    checkLicenceNone(): boolean {
        const licenceType = this.regularDriver$()?.person?.licenceType;

        return licenceType?.code === '0' || licenceType?.description?.toLowerCase() === 'none';
    }

    checkEmploymentAndVehicleUse(): boolean {
        const vData = this.vehicle();
        const employmentStatus = this.regularDriver$()?.person?.employmentStatus;
        const vehicleUse = vData?.vehicle?.use;
        // H - Housewife/Husband AND vechile use "Occasional business" OR "Business use" (Must link to "Private")
        // R - Retired AND vechile use "Occasional business" OR "Business use" (Must be linked to "Private-Retired" in the backend if Private use was selected for vehicle use.
        // N - NOT EMPLOYED DUE TO DISABILITY/ILLNESS AND vechile use "Occasional business" OR "Business use" (Must link to "Private")
        // 03 - Vehicle Use = Business
        // 07 - Vehicle Use = Occasional Business
        return (
            (employmentStatus?.code === 'H' ||
                employmentStatus?.code === 'R' ||
                employmentStatus?.code === 'N') &&
            (vehicleUse?.code === '03' || vehicleUse?.code === '07')
        );
    }

    showComprehensiveCoverDialog(): boolean {
        const vehicle = this.vehicle();
        const isFinanced = vehicle.vehicle?.isFinanced;
        const registeredInSA = vehicle.vehicle?.registeredInSa;
        const coverType = vehicle.vehicle?.coverType;
        return (
            isFinanced === true &&
            registeredInSA === true &&
            !this.coverTypeDialogOpened() &&
            coverType?.code !== CoverType.COMPREHENSIVE &&
            coverType?.code !== CoverType.ADVENSURE &&
            coverType?.code !== CoverType.COMPREHENSIVE_PLUS
        );
    }

    checkAbsoluteAntiTheft(): boolean {
        return this.vehicle()?.vehicle?.trackerDetails?.isAbsoluteAntiTheft ?? false;
    }
}
